import { css, useTheme } from "@emotion/react";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import Heading from "@upsun/react-component-library/src/components/Heading";
import colorsDark from "@upsun/react-component-library/src/styles/colors-dark";
import colorsLight from "@upsun/react-component-library/src/styles/colors-light";
import { UpsunTheme } from "@upsun/react-component-library/src/themes";
import { ReactNode, useRef, useState } from "react";
import Accordion from "@upsun/react-component-library/src/components/Accordion";
import { mediaQueries } from "@upsun/react-component-library/src/styles";
import { spacing } from "@upsun/react-component-library/src/styles/css-utils";
import LottieAnimation from "../LottieAnimation";
import React from "react";

type InfoItem = {
  title: string;
  subtitle: string;
  lottie: string;
  video?: string;
  image?: string;
  children?: ReactNode;
  learnMore?: string;
};

type InfoItemsHomepageProps = {
  data: InfoItem[];
  orientation: "left" | "right";
};

const InfoItemsHomepage: React.FC<InfoItemsHomepageProps> = ({
  data,
  orientation,
  ...props
}) => {
  const theme = useTheme() as UpsunTheme;

  const infoItemStyle = css`
    padding: 32px;
    border: 1px solid transparent;
    border-radius: 32px;

    p {
      margin-bottom: 0 !important;
    }

    &.active {
      background: ${theme.name === "light"
        ? colorsLight.NeutralBgDefault
        : colorsDark.NeutralBgDefault};
    }

    &:hover {
      background: ${theme.name === "light"
        ? colorsLight.NeutralBgDefault
        : colorsDark.NeutralBgDefault};
    }
  `;

  const itemsColumn = css`
    display: flex;
    flex-direction: column;
    gap: 12px;

    order: ${orientation === "right" ? 1 : 2};

    margin-bottom: ${spacing.mobile.s}px;

    ${mediaQueries.md} {
      margin-bottom: ${spacing.tablet.s}px;
    }

    ${mediaQueries.xl} {
      margin-bottom: ${spacing.desktop.s}px;
    }
  `;

  const imageColumn = css`
    order: ${orientation === "right" ? 2 : 1};
  `;

  const subtitleStyles = css`
    color: ${theme.name === "light"
      ? colorsLight.NeutralTxtWeakDefault
      : colorsDark.NeutralTxtWeakDefault};
  `;
  const [counter, setCounter] = useState(0);
  const stateRef = useRef<number>();

  stateRef.current = counter;

  const desktopColumn = css`
    overflow-x: hidden;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: -999;

    ${mediaQueries.xl} {
      position: relative;
      opacity: 1;
      z-index: 0;
    }
  `;

  const mobileColumn = css`
    position: relative;
    top: 0;
    left: 0;
    opacity: 1;
    z-index: 0;

    ${mediaQueries.xl} {
      position: absolute;
      opacity: 0;
      z-index: -999;
    }
  `;

  const containerStyle = css`
    margin-bottom: 80px;

    ${mediaQueries.md} {
      margin-bottom: 96px;
    }

    ${mediaQueries.xl} {
      margin-bottom: 160px;
    }

    summary + span img {
      margin-top: 20px;

      ${mediaQueries.md} {
        margin-top: 24px;
      }
    }
  `;

  return (
    <div css={containerStyle} {...props}>
      <div css={desktopColumn} aria-hidden="true">
        <GridContainer>
          <GridCol
            colStart={orientation === "right" ? 1 : 7}
            colEnd={orientation === "right" ? 7 : 13}
            css={itemsColumn}
          >
            {data.map((item, index) => {
              return (
                <div
                  css={infoItemStyle}
                  className={counter === index ? "active" : ""}
                  onClick={() => {
                    setCounter(index);
                  }}
                  key={`infoItemHomepage-info-${index}`}
                >
                  <Heading level={4} text={item.title} />
                  <p css={subtitleStyles}>{item.subtitle}</p>
                  {item.learnMore && (
                    <>
                      {" "}
                      <a href={item.learnMore}>Learn more</a>
                    </>
                  )}
                </div>
              );
            })}
          </GridCol>
          <GridCol
            colStart={orientation === "right" ? 7 : 1}
            colEnd={orientation === "right" ? 13 : 7}
            css={[
              imageColumn,
              css`
                position: relative;
                div {
                  position: absolute;
                  top: 0;
                  left: 0;
                  min-width: 633.59px;
                  min-height: 633.59px;
                }
              `,
            ]}
          >
            {data.map((item, index) => (
              <div
                key={`infoItemHomepage-animation-${index}`}
                css={css`
                  transition: opacity 1s ease-in-out;
                  opacity: ${index === counter ? 1 : 0};
                `}
              >
                <LottieAnimation
                  src={item.lottie}
                  shouldPlay={index === counter}
                  callbacks={{
                    onComplete: () => {
                      setTimeout(() => {
                        if (stateRef.current === data.length - 1) {
                          setCounter(0);
                        } else {
                          setCounter(stateRef.current! + 1);
                        }
                      }, 1000);
                    },
                  }}
                />
              </div>
            ))}
          </GridCol>
        </GridContainer>
      </div>
      <div css={mobileColumn}>
        <GridContainer
          css={css`
            max-width: 1000px;
          `}
        >
          <GridCol colStart={1} colEnd={13}>
            <Accordion
              firstOpen={true}
              exclusive={true}
              items={data.map((item) => {
                return {
                  header: item.title,
                  text: item.subtitle,
                  children: (
                    <>
                      {item.learnMore && (
                        <>
                          {" "}
                          <a href={item.learnMore}>Learn more</a>
                        </>
                      )}
                      <div
                        css={css`
                          img,
                          video {
                            width: 100%;
                            object-fit: scale-down;
                            object-position: center;
                          }
                        `}
                      >
                        {item.image && <img src={item.image} />}
                        {item.video && (
                          <video
                            autoPlay
                            loop
                            muted
                            playsInline
                          >
                            <source src={`${item.video}-hevc.mp4`} type="video/mp4; codecs=hvc1"/>
                            <source src={`${item.video}.webm`} type="video/webm; codecs=vp9"/>
                            <source src={`${item.video}.mp4`} type="video/mp4"/>
                          </video>
                        )}
                      </div>
                    </>
                  ),
                };
              })}
            />
          </GridCol>
        </GridContainer>
      </div>
    </div>
  );
};

export default InfoItemsHomepage;
